import { RANDOM_CHARACTER_ID, isRandomCharacterId } from "@cold-case/shared/playable-character";
import { TILE_SIZE } from "@cold-case/shared/constants";
import { humanizeId } from "@cold-case/shared/manifest-display";

import { npcRotationTexturePathRotations } from "../lib/asset-mapper.js";

export type PlayableDetective = {
  npc_id: string;
  display_name: string;
  gender: "male" | "female";
};

export const LOBBY_PORTRAIT_PX = Math.round(TILE_SIZE * 1.25 * 1.75);

function portraitUrl(npcId: string): string {
  return npcRotationTexturePathRotations(npcId, "south");
}

function genderLabel(gender: "male" | "female"): string {
  return gender === "male" ? "Male" : "Female";
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function preloadDetectivePortraits(detectives: PlayableDetective[]): void {
  for (const d of detectives) {
    const img = new Image();
    img.src = portraitUrl(d.npc_id);
  }
}

export function effectiveCharacterSelection(selectedId: string | null | undefined): string {
  return selectedId && !isRandomCharacterId(selectedId) ? selectedId : RANDOM_CHARACTER_ID;
}

/** Lobby chip label for a member's detective pick (roster display_name, never raw npc_id). */
export function lobbyDetectiveLabel(
  roster: PlayableDetective[],
  characterNpcId: string | null | undefined
): string {
  if (!characterNpcId) return "…";
  if (isRandomCharacterId(characterNpcId)) return "Random";
  const det = roster.find((d) => d.npc_id === characterNpcId);
  return det?.display_name ?? humanizeId(characterNpcId);
}

function memberPortraitHtml(characterNpcId: string | null | undefined): string {
  const random = isRandomCharacterId(characterNpcId);
  if (random) {
    return `<div class="lobby-card-portrait lobby-card-portrait-random" aria-hidden="true">?</div>`;
  }
  if (characterNpcId) {
    return `<img class="lobby-card-portrait" src="${portraitUrl(characterNpcId)}" alt="" width="${LOBBY_PORTRAIT_PX}" height="${LOBBY_PORTRAIT_PX}" />`;
  }
  return `<div class="lobby-card-portrait lobby-card-portrait-empty" aria-hidden="true"></div>`;
}

export function lobbyMemberCardHtml(
  roster: PlayableDetective[],
  displayName: string,
  characterNpcId: string | null | undefined
): string {
  const pickedSpecific =
    Boolean(characterNpcId) && !isRandomCharacterId(characterNpcId);
  const professionHtml = pickedSpecific
    ? `<p class="lobby-member-profession">Professional ${escapeHtml(lobbyDetectiveLabel(roster, characterNpcId))}</p>`
    : "";
  return `<article class="lobby-member-card">
    ${memberPortraitHtml(characterNpcId)}
    <div class="lobby-member-meta">
      <p class="lobby-member-detective">Detective ${escapeHtml(displayName)}</p>
      ${professionHtml}
    </div>
  </article>`;
}

function portraitPreviewHtml(npcId: string): string {
  if (isRandomCharacterId(npcId)) {
    const markPx = Math.round(LOBBY_PORTRAIT_PX * 0.55);
    return `<div class="detective-portrait-random" style="width:${LOBBY_PORTRAIT_PX}px;height:${LOBBY_PORTRAIT_PX}px;font-size:${markPx}px" aria-hidden="true">?</div>`;
  }
  return `<img class="detective-portrait-img" src="${portraitUrl(npcId)}" alt="" width="${LOBBY_PORTRAIT_PX}" height="${LOBBY_PORTRAIT_PX}" />`;
}

export function renderDetectivePickerHtml(
  detectives: PlayableDetective[],
  selectedId: string | null,
  takenIds: ReadonlySet<string> = new Set()
): string {
  const effective = effectiveCharacterSelection(selectedId);
  const sorted = [...detectives].sort((a, b) => {
    const g = a.gender.localeCompare(b.gender);
    return g !== 0 ? g : a.display_name.localeCompare(b.display_name);
  });

  let options = `<option value="${RANDOM_CHARACTER_ID}"${effective === RANDOM_CHARACTER_ID ? " selected" : ""}>Random</option>`;
  for (const d of sorted) {
    const taken = takenIds.has(d.npc_id) && d.npc_id !== selectedId;
    const label = `${d.display_name} (${genderLabel(d.gender)})`;
    options += `<option value="${escapeHtml(d.npc_id)}"${d.npc_id === effective ? " selected" : ""}${taken ? " disabled" : ""}>${escapeHtml(label)}</option>`;
  }

  return `<div class="detective-picker" role="group" aria-label="Choose your detective">
    <label class="detective-picker-label" for="detective-select">Character</label>
    <div class="detective-picker-row">
      <select id="detective-select" class="detective-select">${options}</select>
      <div class="detective-portrait-preview" id="detective-portrait-preview">${portraitPreviewHtml(effective)}</div>
    </div>
  </div>`;
}

export function bindDetectivePicker(
  container: HTMLElement,
  onPick: (npcId: string) => void
): void {
  const select = container.querySelector<HTMLSelectElement>("#detective-select");
  const previewHost = container.querySelector<HTMLElement>("#detective-portrait-preview");
  if (!select || !previewHost) return;

  select.onchange = () => {
    const id = select.value;
    previewHost.innerHTML = portraitPreviewHtml(id);
    onPick(id);
  };
}

/** @deprecated use renderDetectivePickerHtml */
export const renderDetectiveGridHtml = renderDetectivePickerHtml;

/** @deprecated use bindDetectivePicker */
export const bindDetectiveGrid = bindDetectivePicker;
